import type { DetectedPort, NodeResponse, TaskDetailResponse, VMSize, WorkspaceResponse } from '@simple-agent-manager/shared';
import { VM_SIZE_LABELS } from '@simple-agent-manager/shared';
import { Button, Dialog, Spinner } from '@simple-agent-manager/ui';
import { AlertTriangle, Bot, Box, CheckCircle2, ChevronDown, ChevronUp, Clock, Cloud, Copy, Cpu, ExternalLink, FolderOpen, GitBranch, GitCompare, GitFork, Globe, Hash, MapPin, MessageSquare, RotateCcw, Server, Tag, Timer, User2 } from 'lucide-react';
import { type CSSProperties, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';

import type { ChatSessionResponse } from '../../lib/api';
import { deleteWorkspace, getPortAccessUrl, getProjectTask, updateProjectTaskStatus } from '../../lib/api';
import { stripMarkdown } from '../../lib/text-utils';
import { sanitizeUrl } from '../../lib/url-utils';
import type { SessionState } from './types';
import { formatCountdown } from './types';

/** Labeled value pill used in the session context panel. */
function ContextItem({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-fg-muted min-w-0">
      <span className="shrink-0 opacity-60" aria-hidden="true">{icon}</span>
      <span className="font-medium shrink-0">{label}:</span>
      <span className="text-fg-primary truncate min-w-0">{children}</span>
    </div>
  );
}

/** Human-readable VM size label from shared constants. */
function formatVmSize(size: string): string {
  const config = VM_SIZE_LABELS[size as VMSize];
  return config ? config.label : size;
}

/** Copyable reference ID pill — click to copy the full value, shows truncated display. */
function CopyableId({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [value]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`${label}: ${value} — click to copy`}
      className="inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded border border-[rgba(34,197,94,0.10)] bg-[rgba(8,15,12,0.5)]-default cursor-pointer hover:bg-surface-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent-primary transition-colors min-w-0"
      style={{ color: copied ? 'var(--sam-color-success)' : 'var(--sam-color-fg-muted)' }}
    >
      {icon && <span className="shrink-0 opacity-60" aria-hidden="true">{icon}</span>}
      <span className="shrink-0 text-[10px] font-sans font-medium opacity-70">{label}</span>
      <span className="truncate min-w-0">{value.length > 12 ? `${value.slice(0, 6)}...${value.slice(-4)}` : value}</span>
      <span className="shrink-0" aria-hidden="true">
        {copied ? <CheckCircle2 size={10} /> : <Copy size={10} />}
      </span>
    </button>
  );
}

/** Format a duration in ms to a human-readable string. */
function formatDuration(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) {
    const min = Math.floor(ms / 60_000);
    const sec = Math.round((ms % 60_000) / 1000);
    return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
  }
  const hrs = Math.floor(ms / 3_600_000);
  const min = Math.round((ms % 3_600_000) / 60_000);
  return min > 0 ? `${hrs}h ${min}m` : `${hrs}h`;
}

/** Format a timestamp to a short locale string. */
function formatTime(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Human-readable task execution step. */
function formatExecutionStep(step: string | null | undefined): string | null {
  if (!step) return null;
  const labels: Record<string, string> = {
    node_selection: 'Selecting node',
    node_provisioning: 'Provisioning node',
    workspace_creation: 'Creating workspace',
    workspace_ready: 'Workspace ready',
    attachment_transfer: 'Transferring files',
    agent_session: 'Agent running',
    running: 'Running',
    awaiting_followup: 'Awaiting follow-up',
  };
  return labels[step] ?? step.replace(/_/g, ' ');
}

/** Human-readable agent type label. */
function formatAgentType(agentType: string): string {
  const labels: Record<string, string> = {
    'claude-code': 'Claude Code',
    'openai-codex': 'OpenAI Codex',
  };
  return labels[agentType] ?? agentType;
}

/** Human-readable task mode label. */
function formatTaskMode(mode: string): string {
  return mode === 'conversation' ? 'Conversation' : 'Task';
}

function getWorkspaceProfileLabel(workspace: WorkspaceResponse): string {
  if (workspace.status === 'recovery') return 'Recovery container';
  return workspace.workspaceProfile === 'lightweight' ? 'Lightweight' : 'Full';
}

const RECOVERY_CONTAINER_HELP = 'The devcontainer build failed, so SAM started a fallback recovery container to keep this chat usable. Open the workspace and check Boot Logs for the devcontainer error output.';

function WorkspaceProfileBadge({ workspace }: Readonly<{ workspace: WorkspaceResponse }>) {
  const [open, setOpen] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipId = useId();
  const isRecovery = workspace.status === 'recovery';
  const label = getWorkspaceProfileLabel(workspace);
  const badgeClassName = 'inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0';

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const tooltipWidth = Math.min(280, window.innerWidth - 32);
      const left = Math.max(16, Math.min(rect.right - tooltipWidth, window.innerWidth - tooltipWidth - 16));
      setTooltipStyle({
        position: 'fixed',
        left,
        top: rect.bottom + 4,
        width: tooltipWidth,
        zIndex: 'var(--sam-z-dropdown)' as unknown as number,
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  if (!isRecovery) {
    return (
      <span
        className={badgeClassName}
        aria-label={`Workspace profile: ${label}`}
        style={{
          backgroundColor: workspace.workspaceProfile === 'lightweight' ? 'var(--sam-color-info-tint)' : 'var(--sam-color-success-tint)',
          color: workspace.workspaceProfile === 'lightweight' ? 'var(--sam-color-info)' : 'var(--sam-color-success)',
        }}
      >
        {label}
      </span>
    );
  }

  return (
    <span className="relative inline-flex shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Recovery container: devcontainer build failed"
        aria-describedby={open ? tooltipId : undefined}
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className={`${badgeClassName} border border-transparent cursor-help focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent-primary`}
        style={{
          backgroundColor: 'var(--sam-color-warning-tint, rgba(245, 158, 11, 0.12))',
          color: 'var(--sam-color-warning, #f59e0b)',
          borderColor: 'color-mix(in srgb, var(--sam-color-warning, #f59e0b) 24%, transparent)',
        }}
      >
        <AlertTriangle size={10} aria-hidden="true" />
        {label}
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <span
          id={tooltipId}
          role="tooltip"
          className="rounded-sm glass-surface bg-[rgba(8,15,12,0.94)] px-3 py-2 text-left text-fg-primary shadow-tooltip whitespace-normal pointer-events-none"
          style={{
            fontSize: 'var(--sam-type-caption-size)',
            lineHeight: 'var(--sam-type-caption-line-height)',
            ...tooltipStyle,
          }}
        >
          {RECOVERY_CONTAINER_HELP}
        </span>,
        document.body,
      )}
    </span>
  );
}

/** Collapsible session header — shows title + state dot, with expandable details. */
export function SessionHeader({
  projectId,
  session,
  sessionState,
  loading,
  idleCountdownMs,
  taskEmbed,
  workspace,
  node,
  detectedPorts,
  onSessionMutated,
  onOpenFiles,
  onOpenGit,
  onRetry,
  onFork,
  lineageText,
  hasContentBelow = false,
}: {
  projectId: string;
  session: ChatSessionResponse;
  sessionState: SessionState;
  loading: boolean;
  idleCountdownMs: number | null;
  taskEmbed: ChatSessionResponse['task'] | null;
  workspace: WorkspaceResponse | null;
  node: NodeResponse | null;
  detectedPorts: DetectedPort[];
  onSessionMutated?: () => void;
  onOpenFiles?: () => void;
  onOpenGit?: () => void;
  onRetry?: () => void;
  onFork?: () => void;
  /** Lineage subtitle for retries/forks (e.g., "↩ attempt 3"). */
  lineageText?: string;
  /** When true, suppress bottom rounding and glow (content follows below). */
  hasContentBelow?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);

  // Trigger info — fetched on demand when expanding a task-linked session
  const [triggerDetail, setTriggerDetail] = useState<TaskDetailResponse | null>(null);
  const triggerFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!expanded || !session.taskId || triggerFetchedRef.current === session.taskId) return;
    triggerFetchedRef.current = session.taskId;
    void getProjectTask(projectId, session.taskId).then((detail) => {
      if (detail.trigger) setTriggerDetail(detail);
    }).catch(() => { /* best-effort */ });
  }, [expanded, session.taskId, projectId]);

  // Always show details — we always have at least reference IDs to display
  const hasDetails = true;

  const canMarkComplete = !!(
    taskEmbed?.id &&
    taskEmbed.status !== 'completed' &&
    taskEmbed.status !== 'cancelled' &&
    taskEmbed.status !== 'failed'
  );

  const handleMarkComplete = useCallback(async () => {
    if (!taskEmbed?.id || completing) return;
    setCompleteError(null);
    setCompleting(true);
    setConfirmOpen(false);
    try {
      // 1. Mark the task as completed (this also stops the chat session server-side)
      await updateProjectTaskStatus(projectId, taskEmbed.id, { toStatus: 'completed' });

      // 2. Delete the workspace if one exists
      if (session.workspaceId) {
        await deleteWorkspace(session.workspaceId);
      }

      // Refresh session list via callback instead of full page reload.
      // Reset completing before the callback so the button is not stuck in
      // "Completing..." if the parent's refresh is slower than expected.
      setCompleting(false);
      onSessionMutated?.();
    } catch (err) {
      console.error('Failed to mark task complete:', err);
      setCompleteError(err instanceof Error ? err.message : 'Failed to complete task');
      setCompleting(false);
    }
  }, [projectId, taskEmbed?.id, session.workspaceId, completing, onSessionMutated]);

  return (
    <div
      className={`relative glass-chrome border-t-0 shrink-0${hasContentBelow ? '' : ' rounded-b-2xl after:content-[\'\'] after:absolute after:bottom-0 after:left-[8%] after:right-[8%] after:h-[3px] after:bg-[radial-gradient(ellipse_at_center,rgba(34,197,94,0.55)_0%,transparent_70%)] after:blur-[2px] after:pointer-events-none after:z-10'}`}
      style={{ backgroundColor: 'rgba(8, 15, 12, 0.68)', boxShadow: hasContentBelow ? '0 4px 24px rgba(0, 0, 0, 0.4)' : '0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(34, 197, 94, 0.08)' }}
    >
      {/* Compact row — always visible */}
      <div className="flex items-center gap-2 px-4 py-2 min-h-[44px]">
        <span className="text-sm font-semibold text-fg-primary truncate flex-1 min-w-0">
          {session.topic ? stripMarkdown(session.topic) : `Chat ${session.id.slice(0, 8)}`}
        </span>

        {/* Lineage info for retries/forks */}
        {lineageText && (
          <span
            className="text-[10px] font-medium shrink-0"
            style={{ color: 'var(--sam-color-fg-muted)' }}
            title={lineageText}
          >
            {lineageText}
          </span>
        )}

        {workspace && <WorkspaceProfileBadge workspace={workspace} />}

        {/* Active port badges — shown inline in compact row */}
        {detectedPorts.length > 0 && (
          <span className="inline-flex items-center gap-1 shrink-0">
            {detectedPorts
              .slice()
              .sort((a, b) => a.port - b.port)
              .slice(0, 3) // Show up to 3 port badges inline
              .map((p) => (
                <a
                  key={p.port}
                  href={workspace ? getPortAccessUrl(workspace.id, p.port) : sanitizeUrl(p.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-[10px] font-mono font-medium px-1.5 py-0.5 rounded no-underline shrink-0"
                  style={{
                    backgroundColor: 'var(--sam-color-accent-tint, rgba(59, 130, 246, 0.1))',
                    color: 'var(--sam-color-accent-primary)',
                  }}
                  title={`${p.label} — ${p.url}`}
                >
                  <Globe size={10} />
                  {p.port}
                </a>
              ))}
            {detectedPorts.length > 3 && (
              <span className="text-[10px] text-fg-muted">+{detectedPorts.length - 3}</span>
            )}
          </span>
        )}

        {/* Retry & Fork — always visible when session has a task */}
        {(session.task?.id ?? session.taskId) && (
          <span className="inline-flex items-center gap-0.5 shrink-0">
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                aria-label="Retry task"
                title="Retry — re-run this task"
                className="shrink-0 p-1.5 bg-transparent border-none cursor-pointer text-fg-muted rounded-sm hover:text-fg-primary hover:bg-surface-hover transition-colors"
              >
                <RotateCcw size={14} />
              </button>
            )}
            {onFork && (
              <button
                type="button"
                onClick={onFork}
                aria-label="Fork session"
                title="Fork — start a new task from this session"
                className="shrink-0 p-1.5 bg-transparent border-none cursor-pointer text-fg-muted rounded-sm hover:text-fg-primary hover:bg-surface-hover transition-colors"
              >
                <GitFork size={14} />
              </button>
            )}
          </span>
        )}

        {/* State indicator */}
        <span
          className="inline-flex items-center gap-1 text-xs font-medium shrink-0"
          style={{
            color: sessionState === 'active' ? 'var(--sam-color-success)'
              : sessionState === 'idle' ? 'var(--sam-color-warning, #f59e0b)'
              : 'var(--sam-color-fg-muted)',
          }}
        >
          <span className="w-[6px] h-[6px] rounded-full bg-current" />
          {sessionState === 'active' ? 'Active' : sessionState === 'idle' ? 'Idle' : 'Stopped'}
        </span>

        {/* Background refresh indicator */}
        {loading && (
          <span role="status" aria-label="Refreshing messages" className="inline-flex items-center shrink-0">
            <Spinner size="sm" />
          </span>
        )}

        {/* Expand/collapse toggle — only shown when there are details to show */}
        {hasDetails && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Hide session details' : 'Show session details'}
            className="shrink-0 p-2 bg-transparent border-none cursor-pointer text-fg-muted rounded-sm hover:text-fg-primary transition-colors"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
      </div>

      {/* Expanded details panel */}
      {expanded && hasDetails && (
        <div className="border-t border-[rgba(34,197,94,0.08)] px-4 py-2 space-y-2">
          {/* Reference IDs — copyable pills for cross-referencing */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1 text-[10px] font-medium text-fg-muted uppercase tracking-wide">
              <Hash size={10} />
              References
            </div>
            <div className="flex flex-wrap gap-1.5">
              {taskEmbed?.id && (
                <CopyableId label="Task" value={taskEmbed.id} icon={<Tag size={9} />} />
              )}
              <CopyableId label="Session" value={session.id} icon={<Hash size={9} />} />
              {session.workspaceId && (
                <CopyableId label="Workspace" value={session.workspaceId} />
              )}
              {session.agentSessionId && (
                <CopyableId label="ACP" value={session.agentSessionId} />
              )}
            </div>
          </div>

          {/* Agent info — type, mode, profile */}
          {(session.agentType || taskEmbed?.taskMode || taskEmbed?.agentProfileHint) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted min-w-0">
              {session.agentType && (
                <span className="inline-flex items-center gap-1">
                  <Bot size={11} className="opacity-60" aria-hidden="true" />
                  <span className="font-medium text-fg-primary">{formatAgentType(session.agentType)}</span>
                </span>
              )}
              {taskEmbed?.taskMode && (
                <span className="inline-flex items-center gap-1">
                  {taskEmbed.taskMode === 'conversation'
                    ? <MessageSquare size={11} className="opacity-60" aria-hidden="true" />
                    : <Cpu size={11} className="opacity-60" aria-hidden="true" />
                  }
                  {formatTaskMode(taskEmbed.taskMode)}
                </span>
              )}
              {taskEmbed?.agentProfileHint && (
                <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                  <User2 size={11} className="opacity-60 shrink-0" aria-hidden="true" />
                  <span className="truncate">{taskEmbed.agentProfileHint}</span>
                </span>
              )}
            </div>
          )}

          {/* Task execution status + timing */}
          {(taskEmbed?.id || session.startedAt) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted">
              {/* Task execution step */}
              {taskEmbed?.executionStep && taskEmbed.status === 'in_progress' && (
                <span className="inline-flex items-center gap-1">
                  <Spinner size="sm" />
                  <span className="font-medium" style={{ color: 'var(--sam-color-accent-primary)' }}>
                    {formatExecutionStep(taskEmbed.executionStep)}
                  </span>
                </span>
              )}
              {/* Task status badge */}
              {taskEmbed?.status && (
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: taskEmbed.status === 'completed' ? 'var(--sam-color-success-tint)'
                      : taskEmbed.status === 'failed' ? 'color-mix(in srgb, var(--sam-color-danger) 10%, transparent)'
                      : taskEmbed.status === 'in_progress' ? 'var(--sam-color-accent-tint, rgba(59, 130, 246, 0.1))'
                      : 'var(--sam-color-surface-hover)',
                    color: taskEmbed.status === 'completed' ? 'var(--sam-color-success)'
                      : taskEmbed.status === 'failed' ? 'var(--sam-color-danger)'
                      : taskEmbed.status === 'in_progress' ? 'var(--sam-color-accent-primary)'
                      : 'var(--sam-color-fg-muted)',
                  }}
                >
                  {taskEmbed.status === 'completed' && <CheckCircle2 size={10} />}
                  {taskEmbed.status.charAt(0).toUpperCase() + taskEmbed.status.slice(1).replace(/_/g, ' ')}
                </span>
              )}
              {/* Started time */}
              {session.startedAt && (
                <span className="inline-flex items-center gap-1">
                  <Clock size={11} className="opacity-60" />
                  {formatTime(session.startedAt)}
                </span>
              )}
              {/* Duration */}
              {session.startedAt && (
                <span className="inline-flex items-center gap-1">
                  <Timer size={11} className="opacity-60" />
                  {session.endedAt
                    ? formatDuration(session.endedAt - session.startedAt)
                    : formatDuration(Date.now() - session.startedAt)
                  }
                  {!session.endedAt && <span className="text-[10px] opacity-50">(running)</span>}
                </span>
              )}
            </div>
          )}

          {/* PR link & idle countdown — separate row above buttons */}
          {(taskEmbed?.outputPrUrl || (sessionState === 'idle' && idleCountdownMs !== null)) && (
            <div className="flex items-center gap-3">
              {/* Idle countdown (TDF-8) */}
              {sessionState === 'idle' && idleCountdownMs !== null && (
                <span
                  className="sam-type-caption font-mono"
                  style={{
                    color: idleCountdownMs < 5 * 60 * 1000
                      ? 'var(--sam-color-danger)'
                      : 'var(--sam-color-warning, #f59e0b)',
                  }}
                >
                  Cleanup in {formatCountdown(idleCountdownMs)}
                </span>
              )}

              {/* PR link (T021) */}
              {taskEmbed?.outputPrUrl && (
                <a
                  href={taskEmbed.outputPrUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sam-type-caption font-medium no-underline"
                  style={{ color: 'var(--sam-color-accent-primary)' }}
                >
                  View PR
                </a>
              )}
            </div>
          )}

          {/* Trigger info — shown when task was spawned by an automation trigger */}
          {triggerDetail?.trigger && (
            <div
              className="flex items-start gap-2 px-2 py-1.5 rounded text-xs"
              style={{ background: 'color-mix(in srgb, var(--sam-color-info, #3b82f6) 8%, transparent)' }}
            >
              <Clock size={12} className="shrink-0 mt-0.5" style={{ color: 'var(--sam-color-info, #3b82f6)' }} />
              <div className="flex-1 min-w-0 space-y-0.5">
                <div className="font-medium text-fg-primary">
                  Triggered by: {triggerDetail.trigger.name}
                </div>
                {triggerDetail.trigger.cronHumanReadable && (
                  <div className="text-fg-muted">
                    Schedule: {triggerDetail.trigger.cronHumanReadable}
                  </div>
                )}
                {triggerDetail.triggerExecution && (
                  <div className="text-fg-muted">
                    Run #{triggerDetail.triggerExecution.sequenceNumber}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <Link
                    to={`/projects/${projectId}/triggers/${triggerDetail.trigger.id}`}
                    className="text-accent-primary no-underline hover:underline"
                  >
                    View Trigger
                  </Link>
                  <Link
                    to={`/projects/${projectId}/triggers/${triggerDetail.trigger.id}`}
                    className="text-fg-muted no-underline hover:underline"
                  >
                    All Runs
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Action buttons — wraps on narrow viewports */}
          <div className="flex flex-wrap items-center gap-1.5">
            {session.workspaceId && sessionState === 'active' && (
              <>
                {onOpenFiles && (
                  <Button variant="ghost" size="sm" onClick={onOpenFiles}>
                    <FolderOpen size={14} className="mr-1" />
                    Files
                  </Button>
                )}
                {onOpenGit && (
                  <Button variant="ghost" size="sm" onClick={onOpenGit}>
                    <GitCompare size={14} className="mr-1" />
                    Git
                  </Button>
                )}
                <a
                  href={`/workspaces/${session.workspaceId}`}
                  aria-label="Open workspace"
                  className="no-underline"
                >
                  <Button variant="ghost" size="sm">
                    <ExternalLink size={14} className="mr-1" />
                    Workspace
                  </Button>
                </a>
              </>
            )}

            {canMarkComplete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={completing}
                style={{ color: completing ? undefined : 'var(--sam-color-success)' }}
              >
                <CheckCircle2 size={14} className="mr-1" />
                {completing ? 'Completing...' : 'Complete'}
              </Button>
            )}
          </div>

          {/* Inline error for mark-complete failures */}
          {completeError && (
            <div className="flex items-center gap-2 px-1 py-1">
              <span className="text-xs" style={{ color: 'var(--sam-color-danger)' }}>{completeError}</span>
              <button
                type="button"
                onClick={() => setCompleteError(null)}
                className="text-xs bg-transparent border-none cursor-pointer underline"
                style={{ color: 'var(--sam-color-fg-muted)' }}
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Infrastructure context — workspace & node details */}
          {session.workspaceId && (workspace || node) && (
            <div className="flex flex-col gap-1.5 pt-1 border-t border-border-default">
              {workspace && (
                <>
                  <ContextItem icon={<Box size={12} />} label="Workspace">
                    <a
                      href={`/workspaces/${workspace.id}`}
                      className="no-underline hover:underline"
                      style={{ color: 'var(--sam-color-accent-primary)' }}
                    >
                      {workspace.displayName || workspace.name}
                    </a>
                    <span className="text-fg-muted ml-1">({workspace.status})</span>
                  </ContextItem>
                  <ContextItem icon={<Cpu size={12} />} label="VM Size">
                    {formatVmSize(workspace.vmSize)}
                  </ContextItem>
                </>
              )}
              {node && (
                <>
                  <ContextItem icon={<Server size={12} />} label="Node">
                    <a
                      href={`/nodes/${node.id}`}
                      className="no-underline hover:underline"
                      style={{ color: 'var(--sam-color-accent-primary)' }}
                    >
                      {node.name}
                    </a>
                    {node.healthStatus && (
                      <span
                        className="ml-1"
                        style={{
                          color: node.healthStatus === 'healthy' ? 'var(--sam-color-success)'
                            : node.healthStatus === 'stale' ? 'var(--sam-color-warning, #f59e0b)'
                            : 'var(--sam-color-danger)',
                        }}
                      >
                        ({node.healthStatus})
                      </span>
                    )}
                  </ContextItem>
                  {node.cloudProvider && (
                    <ContextItem icon={<Cloud size={12} />} label="Provider">
                      {node.cloudProvider.charAt(0).toUpperCase() + node.cloudProvider.slice(1)}
                      {workspace?.vmLocation && (
                        <span className="text-fg-muted ml-1">— {workspace.vmLocation}</span>
                      )}
                    </ContextItem>
                  )}
                </>
              )}
              {!node && workspace?.vmLocation && (
                <ContextItem icon={<MapPin size={12} />} label="Location">
                  {workspace.vmLocation}
                </ContextItem>
              )}
              {taskEmbed?.outputBranch && (
                <ContextItem icon={<GitBranch size={12} />} label="Branch">
                  <span className="font-mono text-[11px]">
                    {taskEmbed.outputBranch}
                  </span>
                </ContextItem>
              )}
              {detectedPorts.length > 0 && (
                <ContextItem icon={<Globe size={12} />} label="Ports">
                  <span className="inline-flex flex-wrap gap-1.5">
                    {detectedPorts
                      .slice()
                      .sort((a, b) => a.port - b.port)
                      .map((p) => (
                        <a
                          key={p.port}
                          href={workspace ? getPortAccessUrl(workspace.id, p.port) : sanitizeUrl(p.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-mono text-[11px] no-underline hover:underline"
                          style={{ color: 'var(--sam-color-accent-primary)' }}
                          title={p.label}
                        >
                          {p.port}
                          {p.address === '127.0.0.1' || p.address === '::1' ? ' (local)' : ''}
                          <ExternalLink size={10} />
                        </a>
                      ))}
                  </span>
                </ContextItem>
              )}
            </div>
          )}
          {/* Active ports section — shown when ports are detected and no infrastructure section is shown */}
          {detectedPorts.length > 0 && !(session.workspaceId && (workspace || node)) && (
            <div className="flex flex-col gap-1.5 pt-1 border-t border-border-default">
              <ContextItem icon={<Globe size={12} />} label="Ports">
                <span className="inline-flex flex-wrap gap-1.5">
                  {detectedPorts
                    .slice()
                    .sort((a, b) => a.port - b.port)
                    .map((p) => (
                      <a
                        key={p.port}
                        href={workspace ? getPortAccessUrl(workspace.id, p.port) : sanitizeUrl(p.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-mono text-[11px] no-underline hover:underline"
                        style={{ color: 'var(--sam-color-accent-primary)' }}
                        title={p.label}
                      >
                        {p.port}
                        {p.address === '127.0.0.1' || p.address === '::1' ? ' (local)' : ''}
                        <ExternalLink size={10} />
                      </a>
                    ))}
                </span>
              </ContextItem>
            </div>
          )}
          {/* Fallback when workspace data is still loading or failed */}
          {session.workspaceId && !workspace && !node && (
            <div className="pt-1 border-t border-border-default">
              <span className="text-xs text-fg-muted">Loading infrastructure details...</span>
            </div>
          )}
        </div>
      )}

      {/* Confirmation dialog for mark-complete action */}
      <Dialog isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm">
        <h3 id="dialog-title" className="text-base font-semibold text-fg-primary mb-2">
          Mark task as complete?
        </h3>
        <p className="text-sm text-fg-muted mb-4">
          This will archive the task and delete the workspace. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleMarkComplete}>
            Complete & Delete
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
